import { useTimeoutEffect } from '@react-hookz/web';
import {
	addMilliseconds,
	differenceInMilliseconds,
	differenceInMinutes,
	isAfter,
	isBefore,
	isEqual,
	isSameDay,
	parseISO,
	startOfDay,
	subMilliseconds,
} from 'date-fns';
import { useState } from 'react';

import { useTimeOffset } from '@/cutils/context/TimeContext';
import { notNullOrUndefined } from '@/cutils/misc/notNullOrUndefined';

import { LivestreamVideoFile } from './MediaLivestream';
import { LivestreamUpdateQuery, useLivestreamUpdateQuery } from './__generated__/MediaLivestream.generated';

type LivestreamStatus = 'NOW_LIVE' | 'FUTURE' | 'PRE_LIVE' | 'PAST';

const ONE_MINUTE_IN_MS = 60_000;
const THEN_MINUTES = 10 * ONE_MINUTE_IN_MS;
const THREE_HOURS_IN_MINUTES = 3 * 60;
const THREE_HOURS_IN_MS = THREE_HOURS_IN_MINUTES * ONE_MINUTE_IN_MS;
const THIRTY_MINUTES_IN_MS = 30 * ONE_MINUTE_IN_MS;
const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;
const FIVE_MINUTES_IN_MS = 5 * ONE_MINUTE_IN_MS;

/**
 * This is the maximum safe timeout value that might be used for `setTimeout`. It is
 * approximately 24 days.
 */
const MAX_SAFE_TIMEOUT = Math.pow(2, 32 - 1) - 1;

function isAfterOrEqual(date: Date, compareDate: Date) {
	return isAfter(date, compareDate) || isEqual(date, compareDate);
}

function isBeforeOrEqual(date: Date, compareDate: Date) {
	return isBefore(date, compareDate) || isEqual(date, compareDate);
}

/**
 * Calculates the livestream status based on start and end time.
 *
 * @param startDate
 * @param endDate
 * @param now
 * @param preliveDuration
 * @returns
 */
function getLivestreamStatus(startDate: Date | null, endDate: Date | null, now: Date, preliveDuration: number): LivestreamStatus | null {
	if (startDate !== null && endDate !== null && isAfterOrEqual(now, startDate) && isBeforeOrEqual(now, endDate)) {
		return 'NOW_LIVE';
	} else if (startDate !== null && isBefore(now, startDate)) {
		if (preliveDuration > 0 && differenceInMilliseconds(now, startDate) <= preliveDuration) {
			return 'PRE_LIVE';
		} else {
			return 'FUTURE';
		}
	} else if (endDate !== null && isAfter(now, endDate)) {
		return 'PAST';
	} else {
		return null;
	}
}

/**
 * Returns undefined if the difference is more than the allowed number for setTimeout.
 *
 * @param firstDate
 * @param secondDate
 */
function getSafeDifferenceInMilliseconds(firstDate: Date, secondDate: Date) {
	const difference = differenceInMilliseconds(firstDate, secondDate);

	if (difference > MAX_SAFE_TIMEOUT || difference < 0) {
		return undefined;
	}

	return difference;
}

function getNextLivestreamLabelTimeout(
	startDate: Date | null,
	endDate: Date | null,
	now: Date,
	livestreamState: LivestreamStatus | null
): number | undefined {
	if (livestreamState === null || livestreamState === 'PAST') {
		return undefined;
	}

	if (livestreamState === 'FUTURE' || livestreamState === 'PRE_LIVE') {
		if (startDate === null) {
			return undefined;
		}

		const timeTillLive = differenceInMilliseconds(startDate, now);
		if (timeTillLive <= THEN_MINUTES) {
			// difference is less than 10 minutes so calling setTimeout with it is safe.
			const timeout = timeTillLive % ONE_MINUTE_IN_MS;
			if (timeout === 0 && timeTillLive > 0) {
				return ONE_MINUTE_IN_MS;
			} else {
				return timeout;
			}
		} else if (isSameDay(startDate, now)) {
			// trigger new state update ten minutes before the start time
			return timeTillLive - THEN_MINUTES;
		} else {
			// trigger new state if the day of the livestream is reached
			return getSafeDifferenceInMilliseconds(startOfDay(startDate), now);
		}
	} else {
		if (endDate === null) {
			return undefined;
		} else {
			// got 1 ms after end date, because the end time still has live as current status
			return getSafeDifferenceInMilliseconds(addMilliseconds(endDate, 1), now);
		}
	}
}

/**
 * Returns the timeout when the next status change of the livestream is due based on the current date.
 *
 * @param startDate the start date of the livestream.
 * @param endDate the end date of the livestream
 * @param now the current date
 * @param preliveDuration the prelive duration window in milliseconds
 * @returns
 */
function getNextLivestreamStatusTimeout(
	startDate: Date | null,
	endDate: Date | null,
	now: Date,
	preliveDuration: number
): number | undefined {
	const status = getLivestreamStatus(startDate, endDate, now, preliveDuration);

	switch (status) {
		case 'FUTURE':
			const difference = getSafeDifferenceInMilliseconds(startDate!, now);
			return difference !== undefined ? difference - preliveDuration : undefined;
		case 'PRE_LIVE':
			return getSafeDifferenceInMilliseconds(startDate!, now);
		case 'NOW_LIVE':
			return differenceInMilliseconds(endDate!, now);
		case 'PAST':
			return undefined;
		default:
			return undefined;
	}
}

/**
 * Returns the current date and livestream status every time the livestream status label should change.
 *
 * @param callback the callback to be called
 * @param status the status of the livestream
 * @param options the start date.
 */
export function useLivestreamLabelCountdown(startDate: Date | null, endDate: Date | null) {
	const timeOffset = useTimeOffset();
	const [counter, triggerRender] = useState(0);
	const currentDate = new Date(Date.now() + timeOffset);
	const livestreamState = getLivestreamStatus(startDate, endDate, currentDate, 0);
	const timeout = getNextLivestreamLabelTimeout(startDate, endDate, currentDate, livestreamState);

	useTimeoutEffect(() => {
		triggerRender(counter + 1);
	}, timeout);

	return { currentDate, livestreamState };
}

function mapQueryResult(result: LivestreamUpdateQuery | undefined) {
	const livestream = result?.moduleByRowId?.livestream;

	if (livestream) {
		return {
			startDate: typeof livestream.start === 'string' ? new Date(livestream.start) : null,
			endDate: typeof livestream.end === 'string' ? new Date(livestream.end) : null,
			url: livestream.hls ?? null,
			videos: livestream.programme?.videoFiles?.nodes?.filter(notNullOrUndefined) ?? [],
		};
	} else {
		return undefined;
	}
}

type LivestreamState = {
	startDate: Date | null;
	endDate: Date | null;
	url: string | null;
	videos: LivestreamVideoFile[];
	status: LivestreamStatus | null;
};

/**
 * Returns the poll interval to update the livestream start and end data if they change during the livestream
 * lifecycle.
 *
 * This will return the following configuration:
 * - every 30 minutes if live ist in less than 3 hours or greater than 30 minutes
 * - every 5 minutes if live is in less than 30 minutes.
 * - every 5 minutes for 1 hour if live is in the past and no recordings are available
 *
 *
 * @param livestreamStatus
 * @param startDate
 * @param now
 * @returns
 */
function getPollInterval(
	livestreamStatus: LivestreamStatus | null,
	startDate: Date | null,
	endDate: Date | null,
	now: Date
): { pollInterval: number; timeout: number | undefined } {
	switch (livestreamStatus) {
		case 'FUTURE':
		case 'PRE_LIVE':
			const minutesTillLive = differenceInMinutes(startDate!, now);
			if (minutesTillLive > THREE_HOURS_IN_MINUTES) {
				return {
					pollInterval: 0,
					timeout: getSafeDifferenceInMilliseconds(subMilliseconds(startDate!, THREE_HOURS_IN_MS), now),
				};
			} else if (minutesTillLive > 30) {
				return {
					pollInterval: THIRTY_MINUTES_IN_MS,
					timeout: differenceInMilliseconds(subMilliseconds(startDate!, THIRTY_MINUTES_IN_MS), now),
				};
			} else {
				return { pollInterval: FIVE_MINUTES_IN_MS, timeout: undefined };
			}

		case 'NOW_LIVE':
			return { pollInterval: FIVE_MINUTES_IN_MS, timeout: undefined };

		case 'PAST':
			if (differenceInMinutes(now, endDate!) < 60) {
				return { pollInterval: FIVE_MINUTES_IN_MS, timeout: differenceInMilliseconds(addMilliseconds(endDate!, ONE_HOUR_IN_MS), now) };
			} else {
				return { pollInterval: 0, timeout: undefined };
			}

		default:
			return { pollInterval: 0, timeout: undefined };
	}
}

function getNextTimeout(timeout1: number | undefined, timeout2: number | undefined) {
	if (timeout1 === undefined || timeout2 === undefined) {
		return timeout1 ?? timeout2;
	}

	return Math.min(timeout1, timeout2);
}

/**
 * Hook that updates its state when something regarding the livestream changes.
 *
 * @param rowId
 * @param livestreamStartDate
 * @param livestreamEndDate
 * @param preliveDuration
 * @param url
 * @param videos
 * @returns
 */
export function useLivestreamUpdateStatus(
	rowId: number | 'rundschau',
	livestreamStartDate: string | null,
	livestreamEndDate: string | null,
	preliveDuration: number,
	url: string | null,
	videos: LivestreamVideoFile[]
): LivestreamState {
	const startDate = livestreamStartDate ? parseISO(livestreamStartDate) : null;
	const endDate = livestreamEndDate ? parseISO(livestreamEndDate) : null;

	const timeOffset = useTimeOffset();
	const [counter, triggerRender] = useState(0);
	const currentDate = new Date(Date.now() + timeOffset);
	const statusTimeout = getNextLivestreamStatusTimeout(startDate, endDate, currentDate, preliveDuration);

	const livestreamStatus = getLivestreamStatus(startDate, endDate, currentDate, preliveDuration);

	const { pollInterval, timeout: pollTimeout } = getPollInterval(livestreamStatus, startDate, endDate, currentDate);

	const timeout = getNextTimeout(statusTimeout, pollTimeout);

	useTimeoutEffect(() => {
		triggerRender(counter + 1);
	}, timeout);

	// skip if we show the rundschau, because updating start and end times does not make sense there
	const skipQuery =
		rowId === 'rundschau' ||
		// skip if the status is video files exist, because livestreams durations are usually too long and recordings
		// are added if the livestream ended, however the end time is not modified.
		videos.length > 0;

	const { data, error, loading, previousData } = useLivestreamUpdateQuery({
		variables: { rowId: rowId as number, withVideoOnDemand: livestreamStatus === 'PAST' || livestreamStatus === 'NOW_LIVE' },
		skip: skipQuery,
		pollInterval,
	});

	if (loading || error) {
		const result = mapQueryResult(previousData);
		if (result) {
			return { ...result, status: livestreamStatus };
		}
	} else {
		const result = mapQueryResult(data);
		if (result) {
			return { ...result, status: livestreamStatus };
		}
	}

	return {
		startDate,
		endDate,
		url,
		videos,
		status: livestreamStatus,
	};
}
